import { FileDoneOutlined } from "@ant-design/icons";
import flagsupConstants from "constants/flagsup";
import { actionConstants, appConstants, resourceConstants, roleConstants } from "constants/permission";
import {
  withAssignedSingleSeller,
  withAssignedSiteIds,
  withAuthorizedPlatformsOrSellers,
  withFeatureFlag,
  withPlatformPermission_New,
  withSinglePlatformId,
} from "hocs";
import { lazy } from "react";
import { userUtils } from "utils";
import { t } from "utils/i18n";

const { permission } = userUtils;

const { STAFF_BFF } = appConstants;
const {
  MARKETPLACE_ORDER_READONLY,
  ACCOUNTANT,
  ACCOUNTANT_READONLY,
  SELLER_ORDER_READONLY,
  SYNC_UP_DATA,
  OM_BATCH_UPDATE,
} = roleConstants;
const { RETURN_REQUESTS, PENDING_SALES_ORDERS, WARRANTY_ACTIVATION, EXTERNAL_ORDER } = resourceConstants;
const { READ } = actionConstants;
const { EXTENDED_FLAG_KEYS } = flagsupConstants;

// Pages
const OrderDetailsForPlatform = lazy(() => import("pages/OrderDetail/OrderDetailsForPlatform"));
const MarketOrderList = lazy(() => import("pages/MarketOrderList"));
const SearchOrderList = lazy(() => import("pages/SearchOrderList"));
const SellerOrderList = lazy(() => import("pages/SellerOrderList"));
const OrderDetailsForSeller = lazy(() => import("pages/OrderDetail/OrderDetailsForSeller"));
const ReturnRequestList = lazy(() => import("pages/ReturnRequestList"));
const QuotationList = lazy(() => import("pages/QuotationList"));
const SyncUpData = lazy(() => import("pages/SyncUpData"));
const ImportShipmentsStatus = lazy(() => import("pages/ImportShipmentsStatus"));
const SamsungWarrantyActivationsList = lazy(() => import("pages/SamsungWarrantyActivationsList"));
const SamsungWarrantyActivationsListV2 = lazy(() =>
  import("pages/SamsungWarrantyActivationsList/SamsungWarrantyActivationsListV2")
);
const ExternalOrderTracking = lazy(() => import("pages/ExternalOrderTracking"));
const ExternalOrderImport = lazy(() => import("pages/ExternalOrderTracking/ExternalOrderImport"));

export default [
  // Parent menu
  {
    path: "/orders",
    name: t("Orders.Root"),
    icon: FileDoneOutlined,
    children: [
      "/orders/marketplace",
      "/orders/marketplace/search",
      "/orders/seller",
      "/orders/return-requests",
      "/orders/customer-quotations",
      "/orders/sync",
      "/orders/import-shipments-status",
      "/orders/samsung-warranty-activations",
      "/orders/external-order",
    ],
  },
  // Sub menus
  // -------------------- Order List------------------------------
  {
    exact: true,
    path: "/orders/marketplace",
    name: t("Orders.Marketplace"),
    component: withPlatformPermission_New(MarketOrderList, t("Orders.Root")),
    permissions: [
      permission(STAFF_BFF, MARKETPLACE_ORDER_READONLY),
      // below roles will be removed after https://jira.teko.vn/browse/UX4ERP-1845
      permission(STAFF_BFF, ACCOUNTANT),
      permission(STAFF_BFF, ACCOUNTANT_READONLY),
    ],
  },
  {
    exact: true,
    path: "/orders/seller",
    name: t("Orders.Seller"),
    component: withAssignedSingleSeller(SellerOrderList),
    permissions: [permission(STAFF_BFF, SELLER_ORDER_READONLY)],
    isUseSellerSwitch: true,
  },
  {
    exact: true,
    path: "/orders/marketplace/search",
    name: t("Orders.Search"),
    component: withPlatformPermission_New(SearchOrderList, t("Orders.Root")),
    permissions: [
      permission(STAFF_BFF, MARKETPLACE_ORDER_READONLY),
      // below roles will be removed after https://jira.teko.vn/browse/UX4ERP-1845
      permission(STAFF_BFF, ACCOUNTANT),
      permission(STAFF_BFF, ACCOUNTANT_READONLY),
    ],
  },
  // -------------------- Order Detail ------------------------------
  {
    exact: true,
    path: "/orders/marketplace/:orderId",
    name: t("Orders.Detail"),
    component: withPlatformPermission_New(OrderDetailsForPlatform, t("Orders.Detail")),
    permissions: [
      permission(STAFF_BFF, MARKETPLACE_ORDER_READONLY),
      // below roles will be removed after https://jira.teko.vn/browse/UX4ERP-1845
      permission(STAFF_BFF, ACCOUNTANT),
      permission(STAFF_BFF, ACCOUNTANT_READONLY),
    ],
  },
  {
    exact: true,
    path: "/orders/seller/:sellerId/:orderId",
    name: t("Orders.Detail"),
    component: withAssignedSingleSeller(OrderDetailsForSeller),
    permission: [permission(STAFF_BFF, SELLER_ORDER_READONLY)],
  },
  {
    exact: true,
    path: "/orders/return-requests",
    name: t("Orders.ReturnRequests"),
    component: withAuthorizedPlatformsOrSellers(ReturnRequestList, t("Orders.ReturnRequests")),
    permissions: [permission(STAFF_BFF, RETURN_REQUESTS, READ)],
  },
  {
    exact: true,
    path: "/orders/customer-quotations",
    name: t("Orders.QuotationList"),
    component: withSinglePlatformId(QuotationList, t("Orders.QuotationList")),
    permissions: [permission(STAFF_BFF, PENDING_SALES_ORDERS, READ)],
  },
  {
    exact: true,
    path: "/orders/sync",
    name: t("Orders.SyncUpData"),
    component: SyncUpData,
    permissions: [permission(STAFF_BFF, SYNC_UP_DATA)],
  },
  {
    exact: true,
    path: "/orders/import-shipments-status",
    name: t("Orders.ImportShipmentsStatus"),
    component: ImportShipmentsStatus,
    permissions: [permission(STAFF_BFF, OM_BATCH_UPDATE)],
  },
  {
    exact: true,
    path: "/orders/samsung-warranty-activations",
    name: t("Orders.SamsungWarrantyActivationsList"),
    component: withFeatureFlag(
      SamsungWarrantyActivationsList,
      SamsungWarrantyActivationsListV2,
      EXTENDED_FLAG_KEYS.EPIC_OMNI_1675.key
    ),
    permissions: [permission(STAFF_BFF, WARRANTY_ACTIVATION, READ)],
  },
  {
    exact: true,
    path: "/orders/external-order",
    name: t("ExternalOrderTracking"),
    component: withAssignedSiteIds(ExternalOrderTracking, t("ExternalOrderTracking")),
    permissions: [permission(STAFF_BFF, EXTERNAL_ORDER, READ)],
  },
  {
    exact: true,
    path: "/orders/external-order/import",
    name: t("ImportExternalOrder"),
    component: withAssignedSiteIds(ExternalOrderImport, t("ImportExternalOrder")),
    permissions: [permission(STAFF_BFF, EXTERNAL_ORDER, READ)],
  },
];
